import React, { forwardRef, memo, useCallback, useRef, useState } from "react";
import { Box } from "@chakra-ui/react";
import { Stage, Layer, Line, Image as KonvaImage } from "react-konva";
import { CANVAS_DOT_GAP, CANVAS_DOT_SIZE } from "./answerModes";
import { a } from "../appColors";

// The marking red the paper has always annotated in. It holds up on both
// grounds, so it does not flip with the theme.
/**
 * Room kept clear at the foot of the canvas for the floating action bar.
 *
 * The bar sits over the drawing surface, so anything reaching the bottom
 * edge — a tall figure especially — disappeared behind it.
 */
export const CANVAS_BOTTOM_INSET = 58;

const STROKE_COLOUR = "#df4b26";
const STROKE_WIDTH = 2;

/**
 * The dotted ground, drawn in CSS rather than on the stage.
 *
 * Konva would have to repaint several hundred dots on every stroke; a
 * background gradient is painted once by the compositor and costs nothing
 * while drawing.
 */
const dotted = (colour) => ({
  backgroundImage: `radial-gradient(${colour} ${CANVAS_DOT_SIZE}px, transparent ${CANVAS_DOT_SIZE}px)`,
  backgroundSize: `${CANVAS_DOT_GAP}px ${CANVAS_DOT_GAP}px`,
});

/**
 * Where a student draws their answer (Figma 215:187).
 *
 * Two layers, deliberately: pictures sit on the lower one and ink on the
 * upper, so dragging a diagram does not force the strokes to be re-rastered
 * and a stroke drawn over a picture stays over it.
 */
const AnswerCanvas = forwardRef(({
  width,
  height,
  attachments,
  strokes,
  onAddStroke,
  onMoveAttachment,
  readOnly,
  isDark,
  /**
   * Whether a picture can be dragged around.
   *
   * Off by default: the reason to bring a diagram onto the canvas is to draw
   * on it, and a press that lands on the picture has to start a stroke, not
   * pick the picture up. Repositioning would need its own handle.
   */
  allowMove = false,
  /** "dots" for an answer, "plain" when a figure fills the canvas and the
   *  dots would only show through it. */
  ground = "dots",
  /** The pen the toolbar is set to, so the ink in an answer matches the ink
   *  anywhere else on the page. */
  colour = STROKE_COLOUR,
}, stageRef) => {
  const [liveStroke, setLiveStroke] = useState(null);
  const drawing = useRef(false);

  const pointFrom = (stage) => {
    const pos = stage.getPointerPosition();
    return pos ? [pos.x, pos.y] : null;
  };

  const handleDown = useCallback(
    (e) => {
      if (readOnly) return;
      // Only when pictures can be moved does a press on one mean something
      // other than "start drawing here".
      if (allowMove && e.target !== e.target.getStage()) return;
      const point = pointFrom(e.target.getStage());
      if (!point) return;
      drawing.current = true;
      setLiveStroke({ points: point, colour, strokeWidth: STROKE_WIDTH });
    },
    [readOnly, allowMove, colour]
  );

  const handleMove = useCallback((e) => {
    if (!drawing.current) return;
    const point = pointFrom(e.target.getStage());
    if (!point) return;
    setLiveStroke((prev) => (prev ? { ...prev, points: [...prev.points, ...point] } : prev));
  }, []);

  const handleUp = useCallback(() => {
    if (!drawing.current) return;
    drawing.current = false;
    setLiveStroke((prev) => {
      // A tap is not a stroke.
      if (prev && prev.points.length > 2) onAddStroke(prev);
      return null;
    });
  }, [onAddStroke]);

  const drawable = attachments.filter((att) => att.image);

  return (
    <Box
      position="relative"
      width="100%"
      height={`${height}px`}
      pb={`${CANVAS_BOTTOM_INSET}px`}
      sx={ground === "dots" ? dotted(isDark ? "rgba(255,255,255,0.10)" : "rgba(34,34,34,0.10)") : undefined}
      bg={a.surface}
      cursor={readOnly ? "default" : "crosshair"}
    >
      <Stage
        ref={stageRef}
        width={width}
        height={Math.max(height - CANVAS_BOTTOM_INSET, 1)}
        onMouseDown={handleDown}
        onMouseMove={handleMove}
        onMouseUp={handleUp}
        onMouseLeave={handleUp}
        onTouchStart={handleDown}
        onTouchMove={handleMove}
        onTouchEnd={handleUp}
        style={{ touchAction: "none" }}
      >
        <Layer>
          {drawable.map((att) => (
            <KonvaImage
              key={att.id}
              image={att.image}
              x={att.x || 0}
              y={att.y || 0}
              width={att.width}
              height={att.height}
              draggable={!readOnly && allowMove}
              onDragEnd={(e) => onMoveAttachment(att.id, { x: e.target.x(), y: e.target.y() })}
            />
          ))}
        </Layer>
        <Layer listening={false}>
          {strokes.map((stroke) => (
            <Line
              key={stroke.id}
              points={stroke.points}
              stroke={stroke.colour || STROKE_COLOUR}
              strokeWidth={stroke.strokeWidth || STROKE_WIDTH}
              tension={0.4}
              lineCap="round"
              lineJoin="round"
            />
          ))}
          {liveStroke && (
            <Line
              points={liveStroke.points}
              stroke={liveStroke.colour}
              strokeWidth={liveStroke.strokeWidth}
              tension={0.4}
              lineCap="round"
              lineJoin="round"
            />
          )}
        </Layer>
      </Stage>
    </Box>
  );
});
AnswerCanvas.displayName = "AnswerCanvas";

export default memo(AnswerCanvas);
